import CircularProgress from '@material-ui/core/CircularProgress';
import React, { useEffect, useState, forwardRef } from 'react';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import clsx from 'clsx';
import { isNull, isUndefined, isNil } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Typography from '@material-ui/core/Typography';
import { TextField } from '@material-ui/core';

const LOADING_TEXT = 'Loading, please wait...';

const CustomMenuList = (props) => {
  const { children } = props;
  const { loading, page } = props.selectProps || {};
  return (
    <components.MenuList {...props}>
      {children}
      {loading && page > 1 && (
        <div style={{ padding: 12, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ textAlign: 'center', marginTop: 8, color: '#666', fontSize: 14 }}>{LOADING_TEXT}</div>
        </div>
      )}
    </components.MenuList>
  );
};

// Custom DropdownIndicator to show loading spinner
const CustomDropdownIndicator = (props) => {
  const { loading } = props.selectProps || {};
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', paddingRight: 8 }}>
        <CircularProgress size={18} color="primary" />
      </div>
    );
  }
  return <components.DropdownIndicator {...props} />;
};

const TEXT_SEARCH_MAX_LENGTH = 30;

const useStyles = makeStyles((theme) => ({
  fieldset: {
    borderRadius: 2,
    top: (props) => (props.inline ? 0 : -6),
    left: 0,
    right: 0,
    bottom: 0,
    margin: 0,
    padding: '10px 7px',
    position: 'absolute',
    borderStyle: 'solid',
    borderWidth: 1,
    pointerEvents: 'none',
    borderColor: 'rgba(0,0,0,0.87)',
    opacity: 0.42,
  },
  fieldsetHover: {
    '&:hover fieldset': {
      borderColor: '#535353',
    },
    '&:hover .react-select__indicator *': {
      fill: '#535353',
    },
  },
  legend: {
    fontSize: '0.75em',
    padding: '0 5px',
  },
  legendText: {
    color: 'rgba(0,0,0,0.54)',
  },
  control: {
    // height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: 4,
    // width: '100%'
  },
  container: {
    position: 'relative',
    '& .react-select__clear-indicator': {
      padding: 0,
    },
    '& .react-select__value-container': {
      minHeight: '22px',
    },
  },
  helperText: {
    marginLeft: '14px',
    marginRight: '14px',
    position: 'relative',
  },
  error: {
    '& fieldset': {
      borderColor: theme.input.error,
    },
    '& legend span, & .MuiFormHelperText-root': {
      color: theme.input.error,
    },
  },
  disabled: {
    '&:hover': {
      cursor: 'no-drop',
    },
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.08)',
    },
    '& fieldset legend span, & .react-select__placeholder': {
      color: 'rgba(0, 0, 0, 0.18)',
    },
    '& .react-select__indicator *': {
      fill: 'rgba(0, 0, 0, 0.08)',
    },
    '& .react-select__multi-value': {
      backgroundColor: theme.palette.grey[300],
    },
    '& .react-select__multi-value__remove': {
      display: 'none',
    },
    '& .react-select__multi-value__label': {
      padding: '3px 6px',
    },
    backgroundColor: 'rgba(242,242,242,1.0)',
  },
  inline: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '& > label': {
      position: 'relative',
      transform: 'none',
      alignItems: 'center',
      display: 'flex',
      width: (props) => (!isNil(props.labelSize) ? `${props.labelSize}px` : '200px'),
      color: '#414141',
    },
    // '& input': {
    // 	padding: `${theme.spacing(0.85)}px ${theme.spacing(1.5)}px`,
    // 	fontWeight: 600
    // }
  },
  readonlyText: {
    padding: (props) =>
      props.inline ? `${theme.spacing(0.625)}px 0` : `${theme.spacing(1.875)}px 0 ${theme.spacing(0.625)}px`,
    width: '100%',
    flex: 1,
  },
}));

const MySelect = forwardRef((props, ref) => {
  const {
    label,
    required,
    error,
    helperText,
    onChange,
    value,
    options = [],
    labelKey = 'label',
    valueKey = 'value',
    disabled = false,
    creatable = false,
    async = false,
    onSelect,
    readOnly,
    isMulti,
    inline,
    menuList,
    option,
    customComponents,
    searchable = false,
    bold = true,
    styles = {},
    menuTarget = null,
    ...rest
  } = props;
  const [myValue, setMyValue] = useState(null);
  const [customValue, setCustomValue] = useState(creatable && value ? { label: value, value } : null);
  const Input = (inputProps) => <components.Input {...inputProps} maxLength={TEXT_SEARCH_MAX_LENGTH} />;
  const classes = useStyles(props);
  const customStyles = {
    control: (provided) => ({
      ...provided,
      border: 0,
      borderColor: 'unset',
      boxShadow: 'unset',
      cursor: 'pointer',
      backgroundColor: 'transparent',
      minHeight: inline ? 30 : 40,
      padding: '0 10px',
    }),
    valueContainer: (provided) => {
      return { ...provided, padding: 0 };
    },
    menu: (provided) => {
      return { ...provided, backgroundColor: '#fff', zIndex: 9999 };
    },
    menuList: (provided) => {
      return { ...provided, ...styles.menuList };
    },
    menuPortal: (provided) => {
      return { ...provided, zIndex: 9999 };
    },
    singleValue: (provided) => {
      return { ...provided, fontSize: inline && !searchable ? 12 : 14, fontWeight: inline && !searchable ? 600 : 400 };
    },
    input: (provided) => {
      return { ...provided, fontSize: inline && !searchable ? 12 : 14, '& input': { fontFamily: 'inherit' } };
    },
    placeholder: (provided) => {
      return { ...provided, fontSize: inline ? 12 : 14, fontWeight: 700, opacity: 0.42, color: 'rgba(0,0,0,0.87)' };
    },
    dropdownIndicator: (provided) => {
      return { ...provided, padding: 0, cursor: 'pointer' };
    },
    indicatorSeparator: (provided) => {
      return { ...provided, marginTop: 0, marginBottom: 0, width: 0 };
    },
    clearIndicator: (provided) => {
      return { ...provided.AsyncSelect, cursor: 'pointer' };
    },
    // container: provided => {
    // 	return { ...provided, top: !inline ? '-3px' : 0 };
    // },
    option: (provided) => {
      return { ...provided, cursor: 'pointer' };
    },
  };
  const myOptions = options.map((o) => ({ ...o, label: o[labelKey], value: o[valueKey] }));
  const _ref = ref || props.inputRef || props.innerRef;

  const _onChange = (item) => {
    if (!item && (rest.isClearable || isMulti || item?.isDisabled)) {
      onChange && onChange(null);
      onSelect && onSelect(null);
      return;
    }

    if (item.__isNew__ && creatable) {
      setCustomValue(item);
    }

    if (isMulti) {
      const values = item.map((i) => i.value);
      onChange && onChange(values);
      onSelect && onSelect(item);
      return;
    }

    onChange && onChange(item.value);
    onSelect && onSelect(item);
  };

  const exist = myOptions.find((o) => o.value === customValue?.value);
  if (customValue && creatable && !exist) {
    myOptions.push(customValue);
  }

  useEffect(() => {
    if (value !== '' && !isNull(value) && !isUndefined(value)) {
      // eslint-disable-next-line
      let item = myOptions.filter((i) => (isMulti ? value.includes(i.value) : i.value === value)); //find(myOptions, i => isMulti ? value.includes(i.value) : i.value === value);
      if (item) {
        setMyValue(item);
      } else if (creatable) {
        item = { label: value, value };
        setCustomValue(item);
        setMyValue(item);
      }
    } else {
      setMyValue(null);
    }
    // eslint-disable-next-line
  }, [value, options]);

  const Component = creatable ? CreatableSelect : async ? AsyncSelect : Select;

  const renderControl = ({ showLabel }) => {
    return (
      <div
        className={clsx(
          classes.control,
          error && classes.error,
          disabled && classes.disabled,
          !disabled && classes.fieldsetHover,
          inline && 'flex-1',
        )}
      >
        <div className={clsx(classes.container)}>
          <Component
            className="react-select-container"
            classNamePrefix="react-select"
            styles={customStyles}
            isDisabled={disabled}
            menuPortalTarget={menuTarget}
            menuPlacement="auto"
            {...rest}
            ref={_ref}
            options={myOptions}
            value={myValue}
            onChange={_onChange}
            isMulti={isMulti}
            components={{
              Input,
              DropdownIndicator: CustomDropdownIndicator,
              MenuList: CustomMenuList,
              ...customComponents,
            }}
          />

          <fieldset aria-hidden="true" className={clsx(classes.fieldset)}>
            {showLabel && label && (
              <legend className={clsx(classes.legend)}>
                <span className={clsx(classes.legendText)}>
                  {label}
                  {required && ' *'}
                </span>
              </legend>
            )}
          </fieldset>
        </div>
        {helperText && <FormHelperText className={clsx(classes.helperText)}>{helperText}</FormHelperText>}
      </div>
    );
  };

  const valueText = myValue ? myValue.map((v) => v[labelKey]).join(', ') : '-';
  const renderReadOnlyControl = () => {
    if (!myValue) return '-';

    if (customComponents?.ReadOnlyValue) {
      const ReadOnlyValueComponent = customComponents.ReadOnlyValue;
      return <ReadOnlyValueComponent value={myValue} labelKey={labelKey} valueKey={valueKey} isMulti={isMulti} />;
    }
    return valueText;
  };

  if (inline) {
    return (
      <FormControl fullWidth className={classes.inline}>
        <InputLabel required={required} className={clsx(bold && 'font-bold')}>
          {label}
        </InputLabel>
        {!readOnly && renderControl({ showLabel: false })}
        {readOnly && (
          <Typography className={clsx(classes.readonlyText, 'cursor-default')}>{renderReadOnlyControl()}</Typography>
        )}
      </FormControl>
    );
  }

  if (readOnly) {
    return <TextField readOnly label={label} className="cursor-default" value={valueText} />;
  }

  return renderControl({ showLabel: true });
});

export default MySelect;
